import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';

import { IntlProvider } from 'react-intl';

import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  messages,
  type AppLocale
} from './index';

interface I18nContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function getInitialLocale(): AppLocale {
  try {
    const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);

    if (savedLocale === 'en' || savedLocale === 'ar') {
      return savedLocale;
    }
  } catch {
    // Continue with the default locale if storage is unavailable.
  }

  return DEFAULT_LOCALE;
}

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider = ({ children }: I18nProviderProps) => {
  const [locale, setLocaleState] = useState<AppLocale>(getInitialLocale);

  const setLocale = useCallback((nextLocale: AppLocale) => {
    setLocaleState(nextLocale);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;

    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // Language switching should still work without storage.
    }
  }, [locale]);

  const contextValue = useMemo(
    () => ({ locale, setLocale }),
    [locale, setLocale]
  );

  return (
    <I18nContext.Provider value={contextValue}>
      <IntlProvider
        locale={locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={messages[locale]}
      >
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
};

export function useAppLocale(): I18nContextValue {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useAppLocale must be used within I18nProvider');
  }

  return context;
}
